import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import universityService from "../../services/universityService";

export const fetchUniversities = createAsyncThunk(
  "universities/fetchUniversities",
  async (params, { rejectWithValue }) => {
    try {
      const response = await universityService.getAllUniversities(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch universities"
      );
    }
  }
);

export const createUniversity = createAsyncThunk(
  "universities/createUniversity",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await universityService.createUniversity(formData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create university"
      );
    }
  }
);

export const updateUniversity = createAsyncThunk(
  "universities/updateUniversity",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await universityService.updateUniversity(id, formData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update university"
      );
    }
  }
);

export const deleteUniversity = createAsyncThunk(
  "universities/deleteUniversity",
  async (id, { rejectWithValue }) => {
    try {
      const response = await universityService.deleteUniversity(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete university"
      );
    }
  }
);

const universitySlice = createSlice({
  name: "universities",
  initialState: {
    universities: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUniversities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUniversities.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.universities = responseData.universities || responseData || [];
      })
      .addCase(fetchUniversities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createUniversity.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createUniversity.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newUniversity = responseData.university || responseData;
        if (newUniversity) {
          state.universities.unshift(newUniversity);
        }
        state.success = action.payload.message || "University created successfully";
      })
      .addCase(createUniversity.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      .addCase(updateUniversity.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateUniversity.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedUniversity = responseData.university || responseData;
        const index = state.universities.findIndex((u) => u.id === updatedUniversity.id);
        if (index !== -1) {
          state.universities[index] = updatedUniversity;
        }
        state.success = action.payload.message || "University updated successfully";
      })
      .addCase(updateUniversity.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      .addCase(deleteUniversity.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteUniversity.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.universities = state.universities.filter((u) => u.id !== action.payload.id);
        state.success = action.payload.response.message || "University deleted successfully";
      })
      .addCase(deleteUniversity.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = universitySlice.actions;
export default universitySlice.reducer;
